import { apiClient } from '@/api/client';
import { AdminPlanUpdateDTOStatus, Plan } from '@/api/models';
import { useNotifications } from '@/lib/notifications';
import { queryKeys } from '@/lib/queryKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface PlanFormData {
  businessId: number;
  startAt: string;
  endAt: string;
}

export interface PlanUpdateData {
  status?: AdminPlanUpdateDTOStatus;
  checkInAt?: string | null;
  checkOutAt?: string | null;
}

export interface UserPlanMutationsActions {
  handleCreatePlan: (userId: number, formData: PlanFormData) => Promise<Plan>;
  handleUpdatePlan: (
    userId: number,
    planId: number,
    data: PlanUpdateData,
  ) => Promise<Plan>;
  handleDeletePlan: (userId: number, plan: Plan) => void;
}

export const useUserPlanMutations = () => {
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();

  // Create plan mutation
  const createPlanMutation = useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: number;
      data: PlanFormData;
    }) => {
      const response = await apiClient.POST('/admin/users/{userId}/plans', {
        params: {
          path: { userId },
        },
        body: {
          businessId: data.businessId,
          startAt: data.startAt,
          endAt: data.endAt,
        },
      });

      if (response.error) {
        throw new Error(
          (response.error as { message?: string })?.message ||
            'Failed to create plan',
        );
      }

      return response.data;
    },
    onSuccess: (_, variables) => {
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Plan created successfully',
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_USER_PLANS, variables.userId],
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to create plan',
      });
    },
  });

  // Update plan mutation
  const updatePlanMutation = useMutation({
    mutationFn: async ({
      userId,
      planId,
      data,
    }: {
      userId: number;
      planId: number;
      data: PlanUpdateData;
    }) => {
      const response = await apiClient.PUT(
        '/admin/users/{userId}/plans/{planId}',
        {
          params: {
            path: { userId, planId },
          },
          body: {
            status: data.status,
            checkInAt: data.checkInAt || undefined,
            checkOutAt: data.checkOutAt || undefined,
          },
        },
      );

      if (response.error) {
        throw new Error(
          (response.error as { message?: string })?.message ||
            'Failed to update plan',
        );
      }

      return response.data;
    },
    onSuccess: (_, variables) => {
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Plan updated successfully',
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_USER_PLANS, variables.userId],
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to update plan',
      });
    },
  });

  // Delete plan mutation
  const deletePlanMutation = useMutation({
    mutationFn: async ({
      userId,
      planId,
    }: {
      userId: number;
      planId: number;
    }) => {
      const response = await apiClient.DELETE(
        '/admin/users/{userId}/plans/{planId}',
        {
          params: {
            path: { userId, planId },
          },
        },
      );

      if (response.error) {
        throw new Error('Failed to delete plan');
      }

      return { success: true };
    },
    onSuccess: (_, variables) => {
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Plan deleted successfully',
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_USER_PLANS, variables.userId],
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to delete plan',
      });
    },
  });

  const handleCreatePlan = async (
    userId: number,
    formData: PlanFormData,
  ): Promise<Plan> => {
    return createPlanMutation.mutateAsync({ userId, data: formData });
  };

  const handleUpdatePlan = async (
    userId: number,
    planId: number,
    data: PlanUpdateData,
  ): Promise<Plan> => {
    return updatePlanMutation.mutateAsync({ userId, planId, data });
  };

  const handleDeletePlan = (userId: number, plan: Plan) => {
    const businessName = plan.business?.name || 'Unknown';
    if (
      window.confirm(
        `Are you sure you want to delete the plan at ${businessName}?`,
      )
    ) {
      deletePlanMutation.mutate({ userId, planId: plan.id! });
    }
  };

  const actions: UserPlanMutationsActions = {
    handleCreatePlan,
    handleUpdatePlan,
    handleDeletePlan,
  };

  const isCreating = createPlanMutation.isPending;
  const isUpdating = updatePlanMutation.isPending;
  const isDeleting = deletePlanMutation.isPending;

  return {
    actions,
    isCreating,
    isUpdating,
    isDeleting,
    isLoading: isCreating || isUpdating || isDeleting,
  };
};
